import { motion } from "framer-motion";
import {
  ArrowLeftRight,
  Users,
  Percent,
  Camera,
  MailCheck,
  ShieldCheck,
  History,
} from "lucide-react";
import { useLanguage } from "../context/LanguageContext";

const EASE = [0.16, 1, 0.3, 1];
const ICONS = [ArrowLeftRight, Users, Percent, Camera, MailCheck, ShieldCheck, History];
const SPANS = [
  "md:col-span-7",
  "md:col-span-5",
  "md:col-span-4",
  "md:col-span-4",
  "md:col-span-4",
  "md:col-span-6",
  "md:col-span-6",
];

export const Features = () => {
  const { lang, t } = useLanguage();

  return (
    <section
      id="features"
      data-testid="features-section"
      className="relative bg-[#0D0D0D] py-28 md:py-40"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, ease: EASE }}
          className="mb-20 max-w-3xl"
        >
          <div className="mb-6 flex items-center gap-4">
            <span className="h-px w-12 bg-[#C5A059]" />
            <span className="font-manrope text-xs font-semibold uppercase tracking-[0.22em] text-[#C5A059]">
              {t.features.kicker}
            </span>
          </div>
          <h2 className="font-bebas text-4xl uppercase leading-none tracking-tight text-[#F5F5F5] sm:text-5xl lg:text-6xl">
            {t.features.heading}
          </h2>
          <p className="mt-5 font-cormorant text-xl italic text-[#8A9199] md:text-2xl">
            {t.features.sub}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-12">
          {t.features.items.map((f, i) => {
            const Icon = ICONS[i];
            return (
              <motion.div
                key={`${lang}-${i}`}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.8, delay: (i % 3) * 0.1, ease: EASE }}
                data-testid={`feature-card-${i}`}
                className={`group relative overflow-hidden border border-[#2A2A2A] bg-[#111111] p-8 transition-colors duration-500 hover:border-[#C5A059]/50 md:p-10 ${SPANS[i]}`}
              >
                <div className="absolute -inset-1 bg-gradient-to-r from-[#E65100]/10 to-[#C5A059]/10 opacity-0 blur-2xl transition-opacity duration-700 group-hover:opacity-100" />
                <div className="relative">
                  <div className="flex items-start justify-between">
                    <span className="font-cormorant text-5xl italic text-[#C5A059]/70 md:text-6xl">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <Icon className="h-6 w-6 text-[#4A4D52] transition-colors duration-500 group-hover:text-[#C5A059]" />
                  </div>
                  <h3 className="mt-8 font-bebas text-2xl uppercase tracking-wide text-[#F5F5F5] md:text-3xl">
                    {f.title}
                  </h3>
                  <p className="mt-3 max-w-md font-manrope text-sm leading-relaxed text-[#8A9199]">
                    {f.desc}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
